<script setup lang="ts">
import { useRouter } from 'vue-router';
import { ref } from 'vue';
import type { RobotSummary } from '@/models/RobotSummary';
import formatDate from '@/helpers/DateFormatterHelper';
import type HealthIndicatorEnum from '@/enums/HealthIndicatorEnum';
import UpdateRobotGroupDialog from '@/components/fleet/UpdateRobotGroupDialog.vue';
import {
  healthColor,
  healthIndicatorText,
} from '@/helpers/HealthIndicatorHelper';

defineProps<{ robot: RobotSummary; moveEnabled: boolean }>();
const emit = defineEmits(['robot-moved']);

const router = useRouter();
const updateRobotGroupDialog = ref(false);

function openDetails(robot: RobotSummary) {
  router.push({
    path: '/fleet-management/detail',
    query: { serial_number: robot.serialNumber },
  });
}

function getHealthColor(healthIndicator: HealthIndicatorEnum) {
  return healthColor(healthIndicator);
}

function getHealthString(healthIndicator: HealthIndicatorEnum) {
  return healthIndicatorText(healthIndicator);
}

function doFormatDate(date: string) {
  return formatDate(date, true);
}

const openUpdateGroupDialog = () => {
  updateRobotGroupDialog.value = true;
};
</script>

<template>
  <v-card class="robot-card" @click="openDetails(robot)">
    <div class="robot-card__status-badges">
      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props }">
          <v-icon
            class="mr-2"
            :color="robot.isOnline ? 'success' : 'error'"
            icon="mdi-satellite-uplink"
            v-bind="props"
          />
        </template>
        <span v-if="robot.isOnline">{{ $gettext('Online') }}</span>
        <span v-else>
          {{ $gettext('Last seen') }}: {{ doFormatDate(robot.lastSeen) }}
        </span>
      </v-tooltip>
      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props }">
          <v-icon
            class="mr-2"
            icon="mdi-alarm-light"
            v-bind="props"
            :color="getHealthColor(robot.overallHealthIndicator)"
          />
        </template>
        <span>
          {{ $gettext('Overall health status') }}:
          {{ $gettext(getHealthString(robot.overallHealthIndicator)) }}
        </span>
      </v-tooltip>
    </div>
    <v-card-title>{{ robot.name }}</v-card-title>
    <v-card-subtitle>{{ robot.serialNumber }}</v-card-subtitle>
    <v-card-text class="my-2">
      <div>
        <strong>{{ $gettext('Online status') }}:</strong>
        {{ robot.isOnline ? 'Online' : 'Offline' }}
      </div>
      <div>
        <strong>{{ $gettext('Last seen') }}:</strong>
        {{ doFormatDate(robot.lastSeen) }}
      </div>
      <div class="mr-6">
        <strong>{{ $gettext('Overall health status') }}:</strong>
        {{ $gettext(getHealthString(robot.overallHealthIndicator)) }}
      </div>
      <div
        v-if="moveEnabled"
        class="position-absolute"
        style="right: 16px; bottom: 12px"
      >
        <v-tooltip anchor="bottom" location="bottom">
          <template v-slot:activator="{ props }">
            <v-icon
              v-bind="props"
              icon="mdi-folder-move"
              color="sunrise-apricot"
              @click.stop="openUpdateGroupDialog"
            />
          </template>
          <span>
            {{ $gettext('Move Robot to another group') }}
          </span>
        </v-tooltip>
      </div>
    </v-card-text>
  </v-card>
  <UpdateRobotGroupDialog
    v-model:isOpen="updateRobotGroupDialog"
    :serialNumber="robot.serialNumber"
    @robot-moved="emit('robot-moved', true)"
  />
</template>

<style lang="scss">
.robot-card {
  .v-card-title {
    padding-top: 0;
    padding-bottom: 0;
    margin-top: -12px;
    max-width: calc(100% - 64px);
    margin-right: 68px;
    overflow: initial;
    text-overflow: unset;
    white-space: normal;
    line-height: 1.3rem;
    margin-bottom: 6px;
  }
  &__status-badges {
    display: flex;
    align-items: center;
    justify-content: end;
    margin-right: 8px;
    margin-top: 8px;
  }
}
</style>
